import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";

const inp = "w-full bg-white border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-3 py-2 rounded-sm text-sm text-[#1A1D24]";
const CATS = ["FBO / Ground Handling", "Flight Planning / Weather", "Chauffeur", "Hotel", "Executive Security", "Catering", "Fuel", "Concierge"];
const empty = () => ({ name: "", category: CATS[0], country: "", city: "", airports: [], contact_person: "", email: "", phone: "", rating: 4.5, notes: "" });

export default function OpsSuppliers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [airportsInput, setAirportsInput] = useState("");

  const load = () => api.get("/suppliers").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty(), id: null }); setAirportsInput(""); };
  const openEdit = (s) => { setEditing({ ...s }); setAirportsInput((s.airports || []).join(", ")); };
  const close = () => setEditing(null);

  const save = async () => {
    const { id, ...rest } = editing;
    const payload = { ...rest, airports: airportsInput.split(",").map(s => s.trim().toUpperCase()).filter(Boolean), rating: parseFloat(rest.rating) || null };
    try {
      if (id) await api.put(`/suppliers/${id}`, payload);
      else await api.post(`/suppliers`, payload);
      toast.success("Supplier saved");
      close();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x?.msg || "").filter(Boolean).join("; ") : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <DashboardLayout role="ops">
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="as-overline">Supplier Directory</div>
            <h1 className="as-serif text-5xl font-light mt-2" data-testid="ops-suppliers-title">Vetted network</h1>
          </div>
          <button onClick={openNew} className="as-btn-gold px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2" data-testid="supplier-add-btn"><Plus size={13}/> Add Supplier</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="ops-suppliers-grid">
          {rows.map(s => (
            <div key={s.id} className="as-card p-6" data-testid={`supplier-row-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{s.category}</div>
                  <div className="as-serif text-xl mt-1">{s.name}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {s.rating && <span className="as-mono text-xs as-gold mr-2">★ {s.rating}</span>}
                  <button onClick={() => openEdit(s)} className="p-1.5 text-[#756E64] hover:text-[#A47E2A] hover:bg-[#F1EDE4] rounded-sm" data-testid={`supplier-edit-${s.id}`}><Edit3 size={14}/></button>
                  <button onClick={() => remove(s)} className="p-1.5 text-[#756E64] hover:text-red-600 hover:bg-red-50 rounded-sm" data-testid={`supplier-delete-${s.id}`}><Trash2 size={14}/></button>
                </div>
              </div>
              <div className="as-mono text-[11px] text-[#756E64] mt-2">{s.city}, {s.country}</div>
              <div className="mt-4 pt-4 border-t border-[#E5DFD2] text-xs space-y-1.5">
                <div><span className="text-[#756E64]">Contact:</span> <span className="text-[#1A1D24]">{s.contact_person}</span></div>
                <div><span className="text-[#756E64]">Email:</span> <span className="as-mono">{s.email}</span></div>
                <div><span className="text-[#756E64]">Phone:</span> <span className="as-mono">{s.phone}</span></div>
                {s.airports?.length > 0 && <div><span className="text-[#756E64]">Airports:</span> <span className="as-mono as-gold">{s.airports.join(" · ")}</span></div>}
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={close} data-testid="supplier-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-md border border-[#E5DFD2] w-full max-w-2xl shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-[#E5DFD2]">
                <div className="as-serif text-2xl">{editing.id ? "Edit Supplier" : "Add Supplier"}</div>
                <button onClick={close} className="text-[#756E64] hover:text-[#1A1D24]"><X size={18}/></button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name"><input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} className={inp} data-testid="supplier-name"/></Field>
                <Field label="Category">
                  <select value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} className={inp} data-testid="supplier-category">
                    {CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="City"><input value={editing.city} onChange={e => setEditing({...editing, city: e.target.value})} className={inp}/></Field>
                <Field label="Country"><input value={editing.country} onChange={e => setEditing({...editing, country: e.target.value})} className={inp}/></Field>
                <Field label="Contact Person"><input value={editing.contact_person} onChange={e => setEditing({...editing, contact_person: e.target.value})} className={inp}/></Field>
                <Field label="Rating (0–5)"><input type="number" step="0.1" value={editing.rating || ""} onChange={e => setEditing({...editing, rating: e.target.value})} className={inp}/></Field>
                <Field label="Email"><input value={editing.email} onChange={e => setEditing({...editing, email: e.target.value})} className={inp}/></Field>
                <Field label="Phone"><input value={editing.phone} onChange={e => setEditing({...editing, phone: e.target.value})} className={inp}/></Field>
                <Field label="Airport codes (comma-separated ICAO)" full><input value={airportsInput} onChange={e => setAirportsInput(e.target.value)} placeholder="KTEB, LFPB, EGLF" className={inp}/></Field>
                <Field label="Notes" full><textarea rows="2" value={editing.notes || ""} onChange={e => setEditing({...editing, notes: e.target.value})} className={inp}/></Field>
              </div>
              <div className="p-5 border-t border-[#E5DFD2] flex justify-end gap-3">
                <button onClick={close} className="as-btn-ghost px-4 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest">Cancel</button>
                <button onClick={save} className="as-btn-gold px-5 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="supplier-save">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
function Field({ label, children, full }) {
  return (
    <label className={full ? "md:col-span-2" : ""}>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
